// Login Recipe Wizard: a guided, plain-language flow that walks a non-technical
// operator through building a {label, inputs, steps} recipe.
//
// The wizard owns a simplified internal shape (one HTTP request + one Extract
// + one Capture) and maps it to the full RecipeFormData on submit, so it
// composes with the existing toRecipePayload() serializer + backend wire
// format. Power users who need multi-step flows can author by hand via the
// API; the wizard intentionally constrains the surface for clarity.
import { useMemo } from 'react';

import {
  useWizard,
  useWizardFooterConfig,
  WizardModalFooter,
  WizardProgressBar,
  WizardStepContainer,
  WizardStepIndicator,
} from '@/components/wizards';

import type { RecipeFormData } from './recipePayload';
import type { RecipeWizardData } from './RecipeWizard.types';
import {
  InputsStep,
  OverviewStep,
  RequestStep,
  ReviewStep,
  SessionStep,
} from './steps';
import {
  buildInitialData,
  STEP_IDS,
  WIZARD_STEPS,
  wizardToRecipe,
} from './wizardConfig';

interface RecipeWizardProps {
  webAppUrl: string;
  onSubmit: (recipe: RecipeFormData) => Promise<void>;
  onClose: () => void;
  isSubmitting: boolean;
}

export function RecipeWizard({
  webAppUrl,
  onSubmit,
  onClose,
  isSubmitting,
}: RecipeWizardProps) {
  const initialData = useMemo(() => buildInitialData(webAppUrl), [webAppUrl]);
  const wizard = useWizard<RecipeWizardData>({
    steps: WIZARD_STEPS,
    initialData,
  });

  const footerConfig = useWizardFooterConfig({
    canGoNext: wizard.validation.isCurrentStepValid,
    canGoBack: wizard.navigation.canGoBack,
    isPending: isSubmitting,
    isLastStep: wizard.navigation.isLastStep,
    onNext: wizard.navigation.handleNext,
    onBack: wizard.navigation.handleBack,
    onCancel: onClose,
    onSubmit: () => onSubmit(wizardToRecipe(wizard.formData)),
    labels: {
      submit: 'Save Recipe',
      submitting: 'Saving...',
      next: 'Next',
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <WizardStepIndicator
        currentStep={wizard.progress.currentDisplayStep}
        totalSteps={wizard.progress.totalDisplaySteps}
        stepTitle={wizard.progress.currentStepTitle}
      />
      <WizardProgressBar
        currentStep={wizard.progress.currentDisplayStep}
        totalSteps={wizard.progress.totalDisplaySteps}
        stepTitle={wizard.progress.currentStepTitle}
        showPercentage={false}
      />

      <WizardStepContainer>
        {wizard.currentStepId === STEP_IDS.OVERVIEW && (
          <OverviewStep
            data={wizard.formData}
            setData={wizard.setFormData}
            webAppUrl={webAppUrl}
          />
        )}
        {wizard.currentStepId === STEP_IDS.INPUTS && (
          <InputsStep data={wizard.formData} setData={wizard.setFormData} />
        )}
        {wizard.currentStepId === STEP_IDS.REQUEST && (
          <RequestStep data={wizard.formData} setData={wizard.setFormData} />
        )}
        {wizard.currentStepId === STEP_IDS.SESSION && (
          <SessionStep data={wizard.formData} setData={wizard.setFormData} />
        )}
        {wizard.currentStepId === STEP_IDS.REVIEW && (
          <ReviewStep data={wizard.formData} webAppUrl={webAppUrl} />
        )}
      </WizardStepContainer>

      <WizardModalFooter config={footerConfig} />
    </div>
  );
}
